import React, { useState, useEffect, useCallback } from 'react';
import { 
  ExternalLink, Building2, Mail, Globe, Search, ChevronDown, 
  MapPin, Star, Users, Tag, RefreshCw, Loader2, Plus, X, 
  Heart, Navigation, Filter, Calendar, Lock, ArrowRight, Trophy,
  CheckCircle, AlertCircle, Music, CalendarPlus, Edit2
} from 'lucide-react';

// Import extracted components
import { 
  PrivateLabelCard, 
  PostPrivateLabelModal, 
  EditPrivateLabelModal,
  GENRE_OPTIONS,
  SORT_OPTIONS,
  selectStyle 
} from './LabelConnect';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Sub-tab configuration - UPDATED NAMES
const SUB_TABS = [
  { id: 'labelradar', name: 'Record Labels', icon: Globe, description: 'powered by LabelRadar' },
  { id: 'local', name: 'Local Labels', icon: MapPin, description: 'Labels near you via Google Places' },
  { id: 'private', name: 'Private Labels', icon: Users, description: 'User-submitted labels' },
  { id: 'integration', name: 'LabelRadar Integration', icon: Lock, description: 'Coming soon' }
];

// ============================================
// Featured Labels Component (Accepting Demos)
// ============================================
const FeaturedLabelsSection = () => {
  const [featuredLabels, setFeaturedLabels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeaturedLabels();
  }, []);

  const fetchFeaturedLabels = async () => {
    try {
      // DO NOT fetch LabelRadar labels for accepting demos - static data is not real-time
      // Keep the code ready for when we have API access
      // const lrResponse = await fetch(`${API}/api/labelradar/labels?accepting_demos=true`);
      // const lrData = lrResponse.ok ? await lrResponse.json() : { labels: [] };
      
      // Fetch user labels that are accepting demos (private labels)
      const privateLabelResponse = await fetch(`${API}/api/private-labels?accepting_demos=true`);
      const privateLabelData = privateLabelResponse.ok ? await privateLabelResponse.json() : { labels: [] };
      
      // Fetch registered users (labels/A&R) who are accepting demos
      const userResponse = await fetch(`${API}/api/labels/accepting-demos?limit=10`);
      const userData = userResponse.ok ? await userResponse.json() : { users: [] };
      
      // Combine sources - NO LabelRadar until we have real API data
      const combined = [
        // LabelRadar labels removed - uncomment when API is available:
        // ...(lrData.labels || []).slice(0, 5).map(l => ({ ...l, source: 'labelradar' })),
        ...(privateLabelData.labels || []).map(l => ({ ...l, source: 'private' })),
        ...(userData.users || []).map(u => ({ 
          ...u, 
          source: 'user',
          name: u.company_name || u.name,
          email: u.demo_submission_email
        }))
      ];
      
      setFeaturedLabels(combined);
    } catch (error) {
      console.error('Error fetching featured labels:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return null;
  if (featuredLabels.length === 0) return null;

  return (
    <div style={{
      marginBottom: '1.5rem',
      padding: '1rem',
      background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.1), rgba(147, 51, 234, 0.1))',
      borderRadius: '12px',
      border: '1px solid rgba(251, 191, 36, 0.3)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
        <Star size={18} style={{ color: '#fbbf24' }} fill="#fbbf24" />
        <h3 style={{ margin: 0, color: '#fbbf24', fontSize: '1rem', fontWeight: '600' }}>
          Featured Labels - Accepting Demos
        </h3>
      </div>
      
      <div style={{ display: 'flex', gap: '0.75rem', overflowX: 'auto', paddingBottom: '0.5rem' }}>
        {featuredLabels.map((label, idx) => (
          <a
            key={label.id || `label-${idx}`}
            href={label.source === 'labelradar' 
              ? label.profile_url 
              : label.source === 'user'
                ? `/profile/${label.id}`
                : `/profile/${label.created_by}`}
            target={label.source === 'labelradar' ? '_blank' : '_self'}
            rel="noopener noreferrer"
            style={{
              minWidth: '200px',
              padding: '0.75rem',
              background: 'rgba(30, 20, 50, 0.8)',
              borderRadius: '10px',
              border: '2px solid rgba(251, 191, 36, 0.4)',
              textDecoration: 'none',
              transition: 'all 0.2s ease',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              minHeight: '100px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              {label.image_url || label.logo_url || label.profile_picture ? (
                <img 
                  src={label.image_url || label.logo_url || label.profile_picture} 
                  alt={label.name}
                  style={{ width: '32px', height: '32px', borderRadius: '6px', objectFit: 'cover' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '6px',
                  background: 'rgba(251, 191, 36, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Tag size={16} style={{ color: '#fbbf24' }} />
                </div>
              )}
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, color: '#fff', fontSize: '0.85rem', fontWeight: '600' }}>
                  {label.name}
                </p>
                <p style={{ margin: 0, color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem' }}>
                  {label.source === 'labelradar' ? 'LabelRadar' : label.source === 'user' ? 'Verified User' : 'BeatSeek User'}
                </p>
              </div>
            </div>
            {/* Badge always at bottom with flexbox */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.3rem',
              padding: '0.2rem 0.5rem',
              borderRadius: '4px',
              background: 'rgba(34, 197, 94, 0.2)',
              color: '#22c55e',
              fontSize: '0.7rem',
              fontWeight: '500',
              alignSelf: 'flex-start',
              marginTop: 'auto'
            }}>
              <CheckCircle size={10} />
              Accepting Demos
            </div>
          </a>
        ))}
      </div>
    </div>
  );
};

// ============================================
// Record Labels Tab Component (LabelRadar)
// ============================================
const RecordLabelsTab = () => {
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [genreFilter, setGenreFilter] = useState('all');
  const [sortBy, setSortBy] = useState('fans');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const fetchLabels = useCallback(async (reset = false) => {
    setLoading(true);
    try {
      const currentPage = reset ? 1 : page;
      const response = await fetch(
        `${API}/api/labelradar/labels?page=${currentPage}&genre=${genreFilter}&search=${encodeURIComponent(searchTerm)}&sort=${sortBy}`
      );
      
      if (response.ok) {
        const data = await response.json();
        if (reset) {
          setLabels(data.labels || []);
          setPage(1);
        } else {
          setLabels(prev => [...prev, ...(data.labels || [])]);
        }
        setHasMore(data.has_more || false);
      }
    } catch (error) {
      console.error('Error fetching LabelRadar labels:', error);
    } finally {
      setLoading(false);
    }
  }, [page, genreFilter, searchTerm, sortBy]);

  useEffect(() => {
    fetchLabels(true);
  }, [genreFilter, sortBy]);

  // Debounced search
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchLabels(true);
    }, 500);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  const loadMore = () => {
    setPage(prev => prev + 1);
    fetchLabels(false);
  };

  // Filter labels based on search and genre
  const filteredLabels = labels.filter(label => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      if (!label.name.toLowerCase().includes(search) && 
          !label.country?.toLowerCase().includes(search)) {
        return false;
      }
    }
    if (genreFilter !== 'all') {
      const hasGenre = label.genres?.some(g => 
        g.toLowerCase().includes(genreFilter.toLowerCase())
      );
      if (!hasGenre) return false;
    }
    return true;
  });

  // Sort labels
  const sortedLabels = [...filteredLabels].sort((a, b) => {
    switch (sortBy) {
      case 'fans':
        return (b.fan_count || 0) - (a.fan_count || 0);
      case 'name-asc':
        return a.name.localeCompare(b.name);
      case 'name-desc':
        return b.name.localeCompare(a.name);
      case 'country':
        return (a.country || '').localeCompare(b.country || '');
      default:
        return 0;
    }
  });

  return (
    <div style={{ padding: '1rem' }}>
      {/* Filters Row */}
      <div style={{ 
        display: 'flex', 
        gap: '0.75rem', 
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        {/* Search */}
        <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
          <Search size={16} style={{ 
            position: 'absolute', 
            left: '12px', 
            top: '50%', 
            transform: 'translateY(-50%)',
            color: 'rgba(251, 191, 36, 0.5)' 
          }} />
          <input
            type="text"
            placeholder="Search labels..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '0.6rem 0.75rem 0.6rem 2.5rem',
              borderRadius: '8px',
              border: '1px solid rgba(251, 191, 36, 0.3)',
              background: 'rgba(30, 20, 50, 0.8)',
              color: '#fff',
              fontSize: '0.85rem',
              outline: 'none'
            }}
          />
        </div>

        {/* Genre Filter */}
        <div style={{ position: 'relative' }}>
          <select
            value={genreFilter}
            onChange={(e) => setGenreFilter(e.target.value)}
            style={selectStyle}
          >
            {GENRE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value} style={{ background: '#1a1a2e' }}>
                {opt.label}
              </option>
            ))}
          </select>
          <ChevronDown size={14} style={{ 
            position: 'absolute', 
            right: '8px', 
            top: '50%', 
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            color: 'rgba(251, 191, 36, 0.5)'
          }} />
        </div>

        {/* Sort */}
        <div style={{ position: 'relative' }}>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={selectStyle}
          >
            {SORT_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value} style={{ background: '#1a1a2e' }}>
                {opt.label}
              </option>
            ))}
          </select>
          <ChevronDown size={14} style={{ 
            position: 'absolute', 
            right: '8px', 
            top: '50%', 
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            color: 'rgba(251, 191, 36, 0.5)'
          }} />
        </div>

        {/* Refresh */}
        <button
          onClick={() => fetchLabels(true)}
          disabled={loading}
          style={{
            padding: '0.6rem',
            borderRadius: '8px',
            border: '1px solid rgba(251, 191, 36, 0.3)',
            background: 'rgba(251, 191, 36, 0.1)',
            color: '#fbbf24',
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Results count */}
      <p style={{ 
        fontSize: '0.8rem', 
        color: 'rgba(255, 255, 255, 0.5)', 
        marginBottom: '1rem' 
      }}>
        Showing {sortedLabels.length} labels from LabelRadar
      </p>

      {/* Labels Grid */}
      {loading && labels.length === 0 ? (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          padding: '3rem',
          color: 'rgba(255, 255, 255, 0.5)'
        }}>
          <Loader2 size={24} className="animate-spin" style={{ marginRight: '0.5rem' }} />
          Loading labels from LabelRadar...
        </div>
      ) : sortedLabels.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '3rem',
          color: 'rgba(255, 255, 255, 0.5)'
        }}>
          <Globe size={40} style={{ marginBottom: '1rem', opacity: 0.3 }} />
          <p>No labels found. Try adjusting your filters.</p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '1rem'
        }}>
          {sortedLabels.map((label) => (
            <LabelRadarCard key={label.id} label={label} />
          ))}
        </div>
      )}

      {/* Load More */}
      {hasMore && sortedLabels.length > 0 && (
        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <button
            onClick={loadMore}
            disabled={loading}
            style={{
              padding: '0.75rem 2rem',
              borderRadius: '8px',
              border: '1px solid rgba(251, 191, 36, 0.3)',
              background: 'rgba(251, 191, 36, 0.1)',
              color: '#fbbf24',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
              fontWeight: '500'
            }}
          >
            {loading ? 'Loading...' : 'Load More Labels'}
          </button>
        </div>
      )}
    </div>
  );
};

// LabelRadar Card Component - COMPACT VERSION with inline logo
const LabelRadarCard = ({ label }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const formatFanCount = (count) => {
    if (!count) return 'N/A';
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toString();
  };

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background: isHovered 
          ? 'rgba(40, 30, 60, 0.9)'
          : 'rgba(30, 20, 50, 0.7)',
        borderRadius: '12px',
        border: isHovered 
          ? '2px solid rgba(251, 191, 36, 0.5)' 
          : '2px solid rgba(251, 191, 36, 0.15)',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
        padding: '1rem'
      }}
    >
      {/* Header with inline logo */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
          {/* Conditional inline logo */}
          {label.image_url && !logoError && (
            <img 
              src={label.image_url} 
              alt={label.name}
              style={{ 
                width: '36px', 
                height: '36px', 
                borderRadius: '6px',
                objectFit: 'cover',
                flexShrink: 0
              }}
              onError={() => setLogoError(true)}
            />
          )}
          <h4 style={{ 
            fontSize: '1rem', 
            fontWeight: '600', 
            color: '#fff',
            margin: 0
          }}>
            {label.name}
          </h4>
        </div>
        {label.fan_count && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
            padding: '0.2rem 0.5rem',
            borderRadius: '6px',
            background: 'rgba(251, 191, 36, 0.2)',
            color: '#fbbf24',
            fontSize: '0.7rem',
            fontWeight: '600',
            flexShrink: 0
          }}>
            <Users size={12} />
            {formatFanCount(label.fan_count)}
          </div>
        )}
      </div>

      {/* Country */}
      {label.country && (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.3rem',
          marginBottom: '0.5rem',
          fontSize: '0.75rem',
          color: 'rgba(255, 255, 255, 0.6)'
        }}>
          <MapPin size={12} />
          <span>{label.country}</span>
        </div>
      )}

      {/* Description */}
      {label.description && (
        <p style={{ 
          fontSize: '0.75rem', 
          color: 'rgba(255, 255, 255, 0.5)',
          marginBottom: '0.75rem',
          lineHeight: '1.4',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}>
          {label.description}
        </p>
      )}

      {/* Genres */}
      {label.genres && label.genres.length > 0 && (
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '0.3rem',
          marginBottom: '0.75rem'
        }}>
          {label.genres.slice(0, 4).map((genre, idx) => (
            <span
              key={idx}
              style={{
                padding: '0.15rem 0.4rem',
                borderRadius: '4px',
                background: 'rgba(147, 51, 234, 0.2)',
                color: '#a78bfa',
                fontSize: '0.65rem',
                fontWeight: '500'
              }}
            >
              {genre}
            </span>
          ))}
          {label.genres.length > 4 && (
            <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.4)' }}>
              +{label.genres.length - 4}
            </span>
          )}
        </div>
      )}

      {/* Action Button - ALWAYS links to external LabelRadar */}
      <a
        href={label.profile_url || `https://www.labelradar.com/labels/${label.slug}/profile`}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.4rem',
          padding: '0.5rem',
          borderRadius: '8px',
          background: 'rgba(251, 191, 36, 0.15)',
          border: '1px solid rgba(251, 191, 36, 0.3)',
          color: '#fbbf24',
          textDecoration: 'none',
          fontSize: '0.8rem',
          fontWeight: '500',
          transition: 'all 0.2s ease'
          }}
        >
          <ExternalLink size={14} />
          View on LabelRadar
        </a>
    </div>
  );
};

// ============================================
// Local Labels Tab Component (Google Places) - FIXED
// ============================================
const LocalLabelsTab = () => {
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLocation, setSearchLocation] = useState('');
  const [genreFilter, setGenreFilter] = useState('all');
  const [radius, setRadius] = useState(40234); // 25 miles default
  const [sortBy, setSortBy] = useState('closest');
  const [userLocation, setUserLocation] = useState(null);

  const searchLabels = useCallback(async () => {
    if (!searchLocation.trim()) {
      return;
    }
    
    setLoading(true);
    try {
      // First geocode the location
      const geocodeResponse = await fetch(
        `${API}/api/directory/geocode?address=${encodeURIComponent(searchLocation)}`
      );
      
      if (!geocodeResponse.ok) {
        alert('Could not find that location. Try a different city or zip code.');
        setLoading(false);
        return;
      }
      
      const geocodeData = await geocodeResponse.json();
      if (!geocodeData.latitude || !geocodeData.longitude) {
        alert('Could not find that location.');
        setLoading(false);
        return;
      }
      
      setUserLocation({ lat: geocodeData.latitude, lng: geocodeData.longitude });
      
      // Search for record labels ONLY - exclude studios
      const response = await fetch(
        `${API}/api/directory/places/search?latitude=${geocodeData.latitude}&longitude=${geocodeData.longitude}&radius=${radius}&place_type=record_label&keyword=record+label+music+records+entertainment`
      );
      
      if (response.ok) {
        const data = await response.json();
        // Filter results to only include actual record labels (NOT studios)
        const filteredResults = (data.results || []).filter(place => {
          const name = place.name?.toLowerCase() || '';
          const types = place.types || [];
          
          // Exclude if it's clearly a studio (not a label)
          const isStudio = name.includes('studio') && !name.includes('label') && !name.includes('records');
          if (isStudio) return false;
          
          // Include if name contains label/records keywords
          return name.includes('record') || 
                 name.includes('label') || 
                 name.includes('entertainment') ||
                 name.includes('music group') ||
                 types.includes('record_label');
        });
        setLabels(filteredResults);
      }
    } catch (error) {
      console.error('Error searching local labels:', error);
    } finally {
      setLoading(false);
    }
  }, [searchLocation, radius]);

  // Calculate distance
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Sort labels
  const sortedLabels = [...labels].map(label => ({
    ...label,
    distance: userLocation && label.latitude && label.longitude
      ? calculateDistance(userLocation.lat, userLocation.lng, label.latitude, label.longitude)
      : null
  })).sort((a, b) => {
    switch (sortBy) {
      case 'closest':
        if (a.distance === null) return 1;
        if (b.distance === null) return -1;
        return a.distance - b.distance;
      case 'rating':
        return (b.rating || 0) - (a.rating || 0);
      case 'name-asc':
        return a.name.localeCompare(b.name);
      default:
        return 0;
    }
  });

  return (
    <div style={{ padding: '1rem' }}>
      {/* Search Section */}
      <div style={{
        background: 'rgba(251, 191, 36, 0.05)',
        border: '1px solid rgba(251, 191, 36, 0.2)',
        borderRadius: '12px',
        padding: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.5rem', 
          marginBottom: '1rem' 
        }}>
          <Navigation size={18} style={{ color: '#fbbf24' }} />
          <span style={{ color: '#fbbf24', fontWeight: '600', fontSize: '0.9rem' }}>
            Find Local Record Labels
          </span>
          <span style={{ fontSize: '0.65rem', color: 'rgba(255, 255, 255, 0.4)', fontStyle: 'italic' }}>
            (powered by Google Places)
          </span>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Location Input */}
          <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
            <MapPin size={16} style={{ 
              position: 'absolute', 
              left: '12px', 
              top: '50%', 
              transform: 'translateY(-50%)',
              color: 'rgba(251, 191, 36, 0.5)' 
            }} />
            <input
              type="text"
              placeholder="Enter zip code or city..."
              value={searchLocation}
              onChange={(e) => setSearchLocation(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchLabels()}
              style={{
                width: '100%',
                padding: '0.6rem 0.75rem 0.6rem 2.5rem',
                borderRadius: '8px',
                border: '1px solid rgba(251, 191, 36, 0.3)',
                background: 'rgba(30, 20, 50, 0.8)',
                color: '#fff',
                fontSize: '0.85rem',
                outline: 'none'
              }}
            />
          </div>

          {/* Radius */}
          <div style={{ position: 'relative' }}>
            <select
              value={radius}
              onChange={(e) => setRadius(parseInt(e.target.value))}
              style={selectStyle}
            >
              <option value="8047" style={{ background: '#1a1a2e' }}>Within 5 miles</option>
              <option value="16093" style={{ background: '#1a1a2e' }}>Within 10 miles</option>
              <option value="40234" style={{ background: '#1a1a2e' }}>Within 25 miles</option>
              <option value="80467" style={{ background: '#1a1a2e' }}>Within 50 miles</option>
            </select>
            <ChevronDown size={14} style={{ 
              position: 'absolute', 
              right: '8px', 
              top: '50%', 
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
              color: 'rgba(251, 191, 36, 0.5)'
            }} />
          </div>

          {/* Sort */}
          <div style={{ position: 'relative' }}>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={selectStyle}
            >
              <option value="closest" style={{ background: '#1a1a2e' }}>Closest First</option>
              <option value="rating" style={{ background: '#1a1a2e' }}>Highest Rated</option>
              <option value="name-asc" style={{ background: '#1a1a2e' }}>A-Z</option>
            </select>
            <ChevronDown size={14} style={{ 
              position: 'absolute', 
              right: '8px', 
              top: '50%', 
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
              color: 'rgba(251, 191, 36, 0.5)'
            }} />
          </div>

          {/* Search Button */}
          <button
            onClick={searchLabels}
            disabled={loading || !searchLocation.trim()}
            style={{
              padding: '0.6rem 1.25rem',
              borderRadius: '8px',
              border: 'none',
              background: loading || !searchLocation.trim() 
                ? 'rgba(251, 191, 36, 0.3)' 
                : 'linear-gradient(135deg, #fbbf24, #f59e0b)',
              color: loading || !searchLocation.trim() ? 'rgba(0,0,0,0.5)' : '#000',
              cursor: loading || !searchLocation.trim() ? 'not-allowed' : 'pointer',
              fontSize: '0.85rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem'
            }}
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
            Search
          </button>
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          padding: '3rem',
          color: 'rgba(255, 255, 255, 0.5)'
        }}>
          <Loader2 size={24} className="animate-spin" style={{ marginRight: '0.5rem' }} />
          Searching for local record labels...
        </div>
      ) : sortedLabels.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '3rem',
          color: 'rgba(255, 255, 255, 0.5)'
        }}>
          <MapPin size={40} style={{ marginBottom: '1rem', opacity: 0.3 }} />
          <p>Enter a location above to search for local record labels.</p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '1rem'
        }}>
          {sortedLabels.map((label) => (
            <LocalLabelCard key={label.id} label={label} />
          ))}
        </div>
      )}
    </div>
  );
};

// Local Label Card Component
const LocalLabelCard = ({ label }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background: isHovered 
          ? 'rgba(40, 30, 60, 0.9)'
          : 'rgba(30, 20, 50, 0.7)',
        borderRadius: '12px',
        border: isHovered 
          ? '2px solid rgba(251, 191, 36, 0.5)' 
          : '2px solid rgba(251, 191, 36, 0.15)',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        transform: isHovered ? 'translateY(-2px)' : 'translateY(0)'
      }}
    >
      {/* Image */}
      {label.photo_url && (
        <div style={{ height: '100px', overflow: 'hidden', position: 'relative' }}>
          <img 
            src={label.photo_url} 
            alt={label.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          {label.distance && (
            <div style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              background: 'rgba(0, 0, 0, 0.75)',
              color: '#fbbf24',
              padding: '0.2rem 0.5rem',
              borderRadius: '4px',
              fontSize: '0.7rem',
              fontWeight: '600'
            }}>
              {label.distance.toFixed(1)} mi
            </div>
          )}
        </div>
      )}

      <div style={{ padding: '1rem' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
          <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#fff', margin: 0, flex: 1 }}>
            {label.name}
          </h4>
          {label.rating && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.2rem',
              padding: '0.2rem 0.4rem',
              borderRadius: '6px',
              background: 'rgba(251, 191, 36, 0.2)',
              color: '#fbbf24',
              fontSize: '0.7rem',
              fontWeight: '600'
            }}>
              <Star size={12} fill="#fbbf24" />
              {label.rating.toFixed(1)}
            </div>
          )}
        </div>

        {/* Address */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          gap: '0.3rem',
          marginBottom: '0.75rem',
          fontSize: '0.75rem',
          color: 'rgba(255, 255, 255, 0.6)',
          lineHeight: '1.3'
        }}>
          <MapPin size={12} style={{ flexShrink: 0, marginTop: '2px' }} />
          <span>{label.address}</span>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {label.google_maps_url && (
            <a
              href={label.google_maps_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.3rem',
                padding: '0.4rem',
                borderRadius: '6px',
                background: 'rgba(251, 191, 36, 0.1)',
                border: '1px solid rgba(251, 191, 36, 0.3)',
                color: '#fbbf24',
                textDecoration: 'none',
                fontSize: '0.75rem',
                fontWeight: '500'
              }}
            >
              <MapPin size={12} />
              Maps
            </a>
          )}
          {label.website && (
            <a
              href={label.website}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.3rem',
                padding: '0.4rem',
                borderRadius: '6px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#fff',
                textDecoration: 'none',
                fontSize: '0.75rem',
                fontWeight: '500'
              }}
            >
              <Globe size={12} />
              Website
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// Private Labels Tab Component
// ============================================
const PrivateLabelsTab = () => {
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [genreFilter, setGenreFilter] = useState('all');
  const [showPostModal, setShowPostModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [editingLabel, setEditingLabel] = useState(null);

  useEffect(() => {
    const userStr = localStorage.getItem('beatseek_user');
    if (userStr) {
      try {
        setCurrentUser(JSON.parse(userStr));
      } catch (e) {
        console.error('Failed to parse user:', e);
      }
    }
  }, []);

  const fetchLabels = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API}/api/private-labels?genre=${genreFilter}&search=${encodeURIComponent(searchTerm)}`
      );
      if (response.ok) {
        const data = await response.json();
        setLabels(data.labels || []);
      }
    } catch (error) {
      console.error('Error fetching private labels:', error);
    } finally {
      setLoading(false);
    }
  }, [genreFilter, searchTerm]);

  useEffect(() => {
    fetchLabels();
  }, [fetchLabels]);

  // Filter labels - separate featured (accepting demos) and regular
  const acceptingDemosLabels = labels.filter(label => label.accepting_demos);
  const regularLabels = labels.filter(label => !label.accepting_demos);

  // Apply search/genre filter to both
  const filterLabels = (labelList) => labelList.filter(label => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      if (!label.name.toLowerCase().includes(search)) return false;
    }
    if (genreFilter !== 'all') {
      const hasGenre = label.genres?.some(g => 
        g.toLowerCase().includes(genreFilter.toLowerCase())
      );
      if (!hasGenre) return false;
    }
    return true;
  });

  const filteredAcceptingDemos = filterLabels(acceptingDemosLabels);
  const filteredRegularLabels = filterLabels(regularLabels);

  return (
    <div style={{ padding: '1rem' }}>
      {/* Header with Post Button */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '1rem'
      }}>
        <div>
          <h3 style={{ color: '#fff', fontSize: '1.1rem', margin: 0 }}>Community Labels</h3>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
            Showcase your label to the BeatSeek community
          </p>
        </div>
        {currentUser && (
          <button
            onClick={() => setShowPostModal(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              padding: '0.6rem 1rem',
              borderRadius: '8px',
              border: 'none',
              background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
              color: '#000',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: '600'
            }}
          >
            <Plus size={16} />
            Post Your Label
          </button>
        )}
      </div>

      {/* Filters */}
      <div style={{ 
        display: 'flex', 
        gap: '0.75rem', 
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        {/* Search */}
        <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
          <Search size={16} style={{ 
            position: 'absolute', 
            left: '12px', 
            top: '50%', 
            transform: 'translateY(-50%)',
            color: 'rgba(251, 191, 36, 0.5)' 
          }} />
          <input
            type="text"
            placeholder="Search labels..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '0.6rem 0.75rem 0.6rem 2.5rem',
              borderRadius: '8px',
              border: '1px solid rgba(251, 191, 36, 0.3)',
              background: 'rgba(30, 20, 50, 0.8)',
              color: '#fff',
              fontSize: '0.85rem',
              outline: 'none'
            }}
          />
        </div>

        {/* Genre Filter */}
        <div style={{ position: 'relative' }}>
          <select
            value={genreFilter}
            onChange={(e) => setGenreFilter(e.target.value)}
            style={selectStyle}
          >
            {GENRE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value} style={{ background: '#1a1a2e' }}>
                {opt.label}
              </option>
            ))}
          </select>
          <ChevronDown size={14} style={{ 
            position: 'absolute', 
            right: '8px', 
            top: '50%', 
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            color: 'rgba(251, 191, 36, 0.5)'
          }} />
        </div>

        {/* Refresh */}
        <button
          onClick={fetchLabels}
          disabled={loading}
          style={{
            padding: '0.6rem',
            borderRadius: '8px',
            border: '1px solid rgba(251, 191, 36, 0.3)',
            background: 'rgba(251, 191, 36, 0.1)',
            color: '#fbbf24',
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Info Notice - Simple text */}
      <p style={{
        fontSize: '0.75rem',
        color: 'rgba(255, 255, 255, 0.5)',
        margin: '0 0 1rem 0'
      }}>
        <strong style={{ color: '#a78bfa' }}>Note:</strong> These are user-submitted labels. 
        Want to accept demo submissions? Enable <strong style={{ color: '#a78bfa' }}>&quot;Accepting Demos&quot;</strong> in your profile settings.
      </p>

      {/* Featured Section - Labels Accepting Demos (Gold/Amber Style) */}
      {filteredAcceptingDemos.length > 0 && (
        <div style={{
          marginBottom: '1.5rem',
          padding: '1rem',
          background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.1), rgba(147, 51, 234, 0.1))',
          borderRadius: '12px',
          border: '1px solid rgba(251, 191, 36, 0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
            <Star size={18} style={{ color: '#fbbf24' }} fill="#fbbf24" />
            <h3 style={{ margin: 0, color: '#fbbf24', fontSize: '1rem', fontWeight: '600' }}>
              BeatSeek Featured - Accepting Demos
            </h3>
          </div>
          
          <div style={{ display: 'flex', gap: '0.75rem', overflowX: 'auto', paddingBottom: '0.5rem' }}>
            {filteredAcceptingDemos.map((label) => (
              <div
                key={label.id}
                style={{
                  minWidth: '220px',
                  padding: '0.75rem',
                  background: 'rgba(30, 20, 50, 0.8)',
                  borderRadius: '10px',
                  border: '2px solid rgba(251, 191, 36, 0.4)',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: '120px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  {label.logo_url ? (
                    <img 
                      src={label.logo_url} 
                      alt={label.name}
                      style={{ width: '32px', height: '32px', borderRadius: '6px', objectFit: 'cover' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '6px',
                      background: 'rgba(251, 191, 36, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Tag size={16} style={{ color: '#fbbf24' }} />
                    </div>
                  )}
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, color: '#fff', fontSize: '0.85rem', fontWeight: '600' }}>
                      {label.name}
                    </p>
                    <p style={{ margin: 0, color: '#fbbf24', fontSize: '0.65rem' }}>
                      📬 Accepting Demos
                    </p>
                  </div>
                </div>
                
                {/* Genres */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginBottom: '0.5rem' }}>
                  {(label.genres || []).slice(0, 2).map((genre, idx) => (
                    <span key={idx} style={{
                      background: 'rgba(251, 191, 36, 0.15)',
                      color: '#fbbf24',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '0.6rem'
                    }}>
                      {genre}
                    </span>
                  ))}
                </div>
                
                {/* Contact Button */}
                {(label.demo_submission_email || label.email) && (
                  <a
                    href={`mailto:${label.demo_submission_email || label.email}?subject=${encodeURIComponent('Demo Submission')}`}
                    style={{
                      marginTop: 'auto',
                      padding: '0.4rem',
                      background: 'rgba(251, 191, 36, 0.2)',
                      border: '1px solid rgba(251, 191, 36, 0.4)',
                      borderRadius: '6px',
                      color: '#fbbf24',
                      textDecoration: 'none',
                      fontSize: '0.7rem',
                      fontWeight: '500',
                      textAlign: 'center',
                      display: 'block'
                    }}
                  >
                    Submit Demo
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Labels Grid */}
      {loading ? (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          padding: '3rem',
          color: 'rgba(255, 255, 255, 0.5)'
        }}>
          <Loader2 size={24} className="animate-spin" style={{ marginRight: '0.5rem' }} />
          Loading community labels...
        </div>
      ) : (labels.length === 0) ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '3rem',
          color: 'rgba(255, 255, 255, 0.5)'
        }}>
          <Users size={40} style={{ marginBottom: '1rem', opacity: 0.3 }} />
          <p>No community labels yet. Be the first to showcase your label!</p>
        </div>
      ) : (
        <>
          <h4 style={{ 
            color: 'rgba(255, 255, 255, 0.7)', 
            fontSize: '0.9rem', 
            marginBottom: '1rem',
            fontWeight: '500'
          }}>
            All Community Labels
          </h4>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '1rem'
          }}>
            {filterLabels(labels).map((label) => (
              <PrivateLabelCard 
                key={label.id} 
                label={label}
                currentUser={currentUser}
                onEdit={() => setEditingLabel(label)}
                onRefresh={fetchLabels}
              />
            ))}
          </div>
        </>
      )}

      {/* Post Label Modal with Gradient Border + Scrollbar */}
      {showPostModal && (
        <PostPrivateLabelModal 
          onClose={() => setShowPostModal(false)}
          onSuccess={() => {
            setShowPostModal(false);
            fetchLabels();
          }}
        />
      )}
      
      {/* Edit Label Modal */}
      {editingLabel && (
        <EditPrivateLabelModal
          label={editingLabel}
          onClose={() => setEditingLabel(null)}
          onSuccess={() => {
            setEditingLabel(null);
            fetchLabels();
          }}
        />
      )}
    </div>
  );
};


// ============================================
// Live Contests Tab Component - WITH SUB-TABS
// ============================================
const LiveContestsTab = () => {
  const [activeSubTab, setActiveSubTab] = useState('beatseek');

  return (
    <div style={{ padding: '1rem' }}>
      {/* Sub-tabs - BeatSeek FIRST */}
      <div style={{ 
        display: 'flex', 
        gap: '0.5rem', 
        marginBottom: '1.5rem',
        borderBottom: '1px solid rgba(251, 191, 36, 0.2)',
        paddingBottom: '0.75rem'
      }}>
        <button
          onClick={() => setActiveSubTab('beatseek')}
          style={{
            padding: '0.5rem 1rem',
            borderRadius: '8px 8px 0 0',
            border: 'none',
            background: activeSubTab === 'beatseek' 
              ? 'rgba(147, 51, 234, 0.2)' 
              : 'transparent',
            color: activeSubTab === 'beatseek' ? '#a78bfa' : 'rgba(255,255,255,0.6)',
            cursor: 'pointer',
            fontSize: '0.85rem',
            fontWeight: activeSubTab === 'beatseek' ? '600' : '400',
            borderBottom: activeSubTab === 'beatseek' ? '2px solid #a78bfa' : '2px solid transparent',
            transition: 'all 0.2s ease'
          }}
        >
          BeatSeek Contests
        </button>
        <button
          onClick={() => setActiveSubTab('labelradar')}
          style={{
            padding: '0.5rem 1rem',
            borderRadius: '8px 8px 0 0',
            border: 'none',
            background: activeSubTab === 'labelradar' 
              ? 'rgba(251, 191, 36, 0.2)' 
              : 'transparent',
            color: activeSubTab === 'labelradar' ? '#fbbf24' : 'rgba(255,255,255,0.6)',
            cursor: 'pointer',
            fontSize: '0.85rem',
            fontWeight: activeSubTab === 'labelradar' ? '600' : '400',
            borderBottom: activeSubTab === 'labelradar' ? '2px solid #fbbf24' : '2px solid transparent',
            transition: 'all 0.2s ease'
          }}
        >
          LabelRadar Contests
        </button>
      </div>

      {activeSubTab === 'beatseek' ? (
        <BeatSeekContestsSection />
      ) : (
        <LabelRadarContestsSection />
      )}
    </div>
  );
};

// LabelRadar Contests Section
const LabelRadarContestsSection = () => {
  return (
    <div style={{ textAlign: 'center', padding: '1rem' }}>
      {/* Contest Card */}
      <div style={{
        maxWidth: '500px',
        margin: '0 auto',
        background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.1), rgba(147, 51, 234, 0.1))',
        border: '2px solid rgba(251, 191, 36, 0.3)',
        borderRadius: '16px',
        padding: '2rem',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative gradient */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(90deg, #fbbf24, #a78bfa, #fbbf24)'
        }} />

        {/* Icon */}
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: 'rgba(251, 191, 36, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 1.5rem'
        }}>
          <Trophy size={40} style={{ color: '#fbbf24' }} />
        </div>

        {/* Title */}
        <h3 style={{ 
          color: '#fff', 
          fontSize: '1.5rem', 
          fontWeight: '700',
          marginBottom: '1rem'
        }}>
          LabelRadar Live Contests
        </h3>

        {/* Description */}
        <p style={{ 
          color: 'rgba(255, 255, 255, 0.7)', 
          fontSize: '0.95rem',
          lineHeight: '1.6',
          marginBottom: '1.5rem'
        }}>
          View active remix contests, demo submission opportunities, and production challenges 
          from labels on LabelRadar.
        </p>

        {/* Notice */}
        <div style={{
          background: 'rgba(251, 191, 36, 0.1)',
          border: '1px solid rgba(251, 191, 36, 0.3)',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1.5rem'
        }}>
          <p style={{ 
            color: 'rgba(255, 255, 255, 0.8)', 
            fontSize: '0.85rem',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}>
            <Lock size={16} style={{ color: '#fbbf24' }} />
            <span>Registration / Login required to view live contests</span>
          </p>
        </div>

        {/* CTA Button */}
        <a
          href="https://www.labelradar.com/artists/to-contests"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.875rem 2rem',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
            color: '#000',
            textDecoration: 'none',
            fontSize: '1rem',
            fontWeight: '700',
            transition: 'all 0.3s ease'
          }}
        >
          View Contests on LabelRadar
          <ArrowRight size={18} />
        </a>
      </div>
    </div>
  );
};

// BeatSeek Contests Section
const BeatSeekContestsSection = () => {
  const [contests, setContests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPostModal, setShowPostModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [genreFilter, setGenreFilter] = useState('all');
  const [addingToCalendar, setAddingToCalendar] = useState(null);
  const [addedToCalendar, setAddedToCalendar] = useState([]);

  useEffect(() => {
    const userStr = localStorage.getItem('beatseek_user');
    if (userStr) {
      try {
        setCurrentUser(JSON.parse(userStr));
      } catch (e) {
        console.error('Failed to parse user:', e);
      }
    }
    fetchContests();
  }, []);

  const fetchContests = async () => {
    try {
      const response = await fetch(`${API}/api/remix-contests`);
      if (response.ok) {
        const data = await response.json();
        setContests(data.contests || []);
      }
    } catch (error) {
      console.error('Error fetching contests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePostClick = () => {
    if (!currentUser) {
      alert('Please log in to post a contest.');
      return;
    }
    // Check if user is a registered label
    if (!currentUser.verified_label_ar && !currentUser.user_type?.includes('label')) {
      alert('Only registered label users can post contests. Please update your profile to register as a label/A&R.');
      return;
    }
    setShowPostModal(true);
  };

  const handleAddToCalendar = async (contest) => {
    if (!currentUser) {
      alert('Please log in to add events to your calendar.');
      return;
    }

    setAddingToCalendar(contest.id);
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${API}/api/remix-contests/${contest.id}/add-to-calendar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setAddedToCalendar(prev => [...prev, contest.id]);
        alert('Contest added to your Gig & Event Hub calendar! You will receive a reminder 48 hours before the deadline.');
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Failed to add to calendar'}`);
      }
    } catch (error) {
      console.error('Error adding to calendar:', error);
      alert('Failed to add to calendar');
    } finally {
      setAddingToCalendar(null);
    }
  };

  // Filter contests
  const filteredContests = contests.filter(contest => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      if (!contest.title?.toLowerCase().includes(search) && 
          !contest.label_name?.toLowerCase().includes(search)) {
        return false;
      }
    }
    if (genreFilter !== 'all') {
      const hasGenre = contest.genres?.some(g => 
        g.toLowerCase().includes(genreFilter.toLowerCase())
      );
      if (!hasGenre) return false;
    }
    return true;
  });

  return (
    <div>
      {/* Header with Post Button */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '1rem'
      }}>
        <div>
          <h3 style={{ color: '#fff', fontSize: '1.1rem', margin: 0 }}>BeatSeek Remix Contests</h3>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
            Community remix contests posted by registered labels
          </p>
        </div>
        <button
          onClick={handlePostClick}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            padding: '0.6rem 1rem',
            borderRadius: '8px',
            border: 'none',
            background: 'linear-gradient(135deg, #a78bfa, #8b5cf6)',
            color: '#fff',
            cursor: 'pointer',
            fontSize: '0.85rem',
            fontWeight: '600'
          }}
        >
          <Plus size={16} />
          Post Contest
        </button>
      </div>

      {/* Filters */}
      <div style={{ 
        display: 'flex', 
        gap: '0.75rem', 
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
          <Search size={16} style={{ 
            position: 'absolute', 
            left: '12px', 
            top: '50%', 
            transform: 'translateY(-50%)',
            color: 'rgba(147, 51, 234, 0.5)' 
          }} />
          <input
            type="text"
            placeholder="Search contests..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '0.6rem 0.75rem 0.6rem 2.5rem',
              borderRadius: '8px',
              border: '1px solid rgba(147, 51, 234, 0.3)',
              background: 'rgba(30, 20, 50, 0.8)',
              color: '#fff',
              fontSize: '0.85rem',
              outline: 'none'
            }}
          />
        </div>
        <div style={{ position: 'relative' }}>
          <select
            value={genreFilter}
            onChange={(e) => setGenreFilter(e.target.value)}
            style={{
              ...selectStyle,
              border: '1px solid rgba(147, 51, 234, 0.3)'
            }}
          >
            {GENRE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value} style={{ background: '#1a1a2e' }}>
                {opt.label}
              </option>
            ))}
          </select>
          <ChevronDown size={14} style={{ 
            position: 'absolute', 
            right: '8px', 
            top: '50%', 
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            color: 'rgba(147, 51, 234, 0.5)'
          }} />
        </div>
      </div>

      {/* Contests List */}
      {loading ? (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          padding: '3rem',
          color: 'rgba(255, 255, 255, 0.5)'
        }}>
          <Loader2 size={24} className="animate-spin" style={{ marginRight: '0.5rem' }} />
          Loading contests...
        </div>
      ) : filteredContests.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '3rem',
          color: 'rgba(255, 255, 255, 0.5)'
        }}>
          <Trophy size={40} style={{ marginBottom: '1rem', opacity: 0.3 }} />
          <p>No remix contests yet. Be the first to post one!</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {filteredContests.map((contest) => (
            <div
              key={contest.id}
              style={{
                background: 'rgba(30, 20, 50, 0.7)',
                borderRadius: '12px',
                border: '2px solid rgba(147, 51, 234, 0.2)',
                padding: '1.25rem',
                transition: 'all 0.2s ease'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                <div>
                  <h4 style={{ margin: '0 0 0.25rem 0', color: '#fff', fontSize: '1.1rem', fontWeight: '600' }}>
                    {contest.title}
                  </h4>
                  <p style={{ margin: 0, color: '#a78bfa', fontSize: '0.8rem' }}>
                    by {contest.label_name || 'Unknown Label'}
                  </p>
                </div>
                {contest.deadline && (
                  <div style={{
                    padding: '0.25rem 0.75rem',
                    borderRadius: '6px',
                    background: 'rgba(239, 68, 68, 0.2)',
                    color: '#ef4444',
                    fontSize: '0.75rem',
                    fontWeight: '600'
                  }}>
                    Deadline: {new Date(contest.deadline).toLocaleDateString()}
                  </div>
                )}
              </div>

              {contest.description && (
                <p style={{ 
                  color: 'rgba(255, 255, 255, 0.7)', 
                  fontSize: '0.85rem',
                  marginBottom: '0.75rem',
                  lineHeight: '1.5'
                }}>
                  {contest.description}
                </p>
              )}

              {/* Genres */}
              {contest.genres && contest.genres.length > 0 && (
                <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                  {contest.genres.map((genre, idx) => (
                    <span key={idx} style={{
                      padding: '0.15rem 0.5rem',
                      borderRadius: '4px',
                      background: 'rgba(147, 51, 234, 0.2)',
                      color: '#a78bfa',
                      fontSize: '0.7rem'
                    }}>
                      {genre}
                    </span>
                  ))}
                </div>
              )}

              {/* Prize */}
              {contest.prize && (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.3rem',
                  marginBottom: '0.75rem',
                  color: '#fbbf24',
                  fontSize: '0.85rem'
                }}>
                  <Trophy size={14} />
                  <span>Prize: {contest.prize}</span>
                </div>
              )}

              {/* Actions */}
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                <button
                  onClick={() => handleAddToCalendar(contest)}
                  disabled={addingToCalendar === contest.id || addedToCalendar.includes(contest.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.3rem',
                    padding: '0.5rem 1rem',
                    borderRadius: '8px',
                    border: '1px solid rgba(147, 51, 234, 0.3)',
                    background: addedToCalendar.includes(contest.id) 
                      ? 'rgba(34, 197, 94, 0.2)' 
                      : 'rgba(147, 51, 234, 0.1)',
                    color: addedToCalendar.includes(contest.id) ? '#22c55e' : '#a78bfa',
                    cursor: addingToCalendar === contest.id || addedToCalendar.includes(contest.id) 
                      ? 'not-allowed' 
                      : 'pointer',
                    fontSize: '0.8rem',
                    fontWeight: '500'
                  }}
                >
                  {addingToCalendar === contest.id ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : addedToCalendar.includes(contest.id) ? (
                    <CheckCircle size={14} />
                  ) : (
                    <CalendarPlus size={14} />
                  )}
                  {addedToCalendar.includes(contest.id) ? 'Added to Calendar' : 'Add to Calendar'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Post Contest Modal */}
      {showPostModal && (
        <PostContestModal 
          onClose={() => setShowPostModal(false)}
          onSuccess={() => {
            setShowPostModal(false);
            fetchContests();
          }}
        />
      )}
    </div>
  );
};

// Post Contest Modal
const PostContestModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    genres: '',
    deadline: '',
    prize: '',
    rules: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      alert('Please enter a contest title');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const genresArray = formData.genres.split(',').map(g => g.trim()).filter(g => g);
      
      const response = await fetch(`${API}/api/remix-contests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: formData.title,
          description: formData.description,
          genres: genresArray,
          deadline: formData.deadline || null,
          prize: formData.prize || null,
          rules: formData.rules || null
        })
      });

      if (response.ok) {
        onSuccess();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Failed to post contest'}`);
      }
    } catch (error) {
      console.error('Error posting contest:', error);
      alert('Failed to post contest');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10005,
      padding: '1rem'
    }}
    onClick={onClose}
    >
      <div
        className="post-contest-modal"
        style={{
          maxWidth: '500px',
          width: '100%',
          background: 'linear-gradient(135deg, rgba(20, 10, 35, 0.98), rgba(30, 15, 50, 0.98)) padding-box, linear-gradient(135deg, #9333ea, #fbbf24, #9333ea, #fbbf24) border-box',
          borderRadius: '16px',
          border: '3px solid transparent',
          maxHeight: '85vh',
          overflow: 'auto',
          backgroundSize: '200% 200%',
          animation: 'borderFlow 4s linear infinite'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '1rem 1.25rem',
          borderBottom: '1px solid rgba(147, 51, 234, 0.2)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{ margin: 0, color: '#a78bfa', fontSize: '1.1rem' }}>
            Post Remix Contest
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'rgba(255,255,255,0.5)',
              cursor: 'pointer',
              padding: '0.25rem'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '1.25rem' }}>
          {/* Contest Title */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.3rem', color: '#a78bfa', fontSize: '0.8rem' }}>
              Contest Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Official Remix Contest - Track Name"
              required
              style={{
                width: '100%',
                padding: '0.6rem',
                borderRadius: '8px',
                border: '1px solid rgba(147, 51, 234, 0.3)',
                background: 'rgba(30, 20, 50, 0.8)',
                color: '#fff',
                fontSize: '0.85rem',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.3rem', color: '#a78bfa', fontSize: '0.8rem' }}>
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the contest, requirements, and how to enter..."
              rows={3}
              style={{
                width: '100%',
                padding: '0.6rem',
                borderRadius: '8px',
                border: '1px solid rgba(147, 51, 234, 0.3)',
                background: 'rgba(30, 20, 50, 0.8)',
                color: '#fff',
                fontSize: '0.85rem',
                outline: 'none',
                resize: 'vertical',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Genres */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.3rem', color: '#a78bfa', fontSize: '0.8rem' }}>
              Genres (comma-separated)
            </label>
            <input
              type="text"
              value={formData.genres}
              onChange={(e) => setFormData({ ...formData, genres: e.target.value })}
              placeholder="House, Tech House, Techno"
              style={{
                width: '100%',
                padding: '0.6rem',
                borderRadius: '8px',
                border: '1px solid rgba(147, 51, 234, 0.3)',
                background: 'rgba(30, 20, 50, 0.8)',
                color: '#fff',
                fontSize: '0.85rem',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Deadline & Prize */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.3rem', color: '#a78bfa', fontSize: '0.8rem' }}>
                Deadline
              </label>
              <input
                type="date"
                value={formData.deadline}
                onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.6rem',
                  borderRadius: '8px',
                  border: '1px solid rgba(147, 51, 234, 0.3)',
                  background: 'rgba(30, 20, 50, 0.8)',
                  color: '#fff',
                  fontSize: '0.85rem',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.3rem', color: '#a78bfa', fontSize: '0.8rem' }}>
                Prize
              </label>
              <input
                type="text"
                value={formData.prize}
                onChange={(e) => setFormData({ ...formData, prize: e.target.value })}
                placeholder="e.g., Official Release"
                style={{
                  width: '100%',
                  padding: '0.6rem',
                  borderRadius: '8px',
                  border: '1px solid rgba(147, 51, 234, 0.3)',
                  background: 'rgba(30, 20, 50, 0.8)',
                  color: '#fff',
                  fontSize: '0.85rem',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          {/* Rules */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.3rem', color: '#a78bfa', fontSize: '0.8rem' }}>
              Rules & Requirements
            </label>
            <textarea
              value={formData.rules}
              onChange={(e) => setFormData({ ...formData, rules: e.target.value })}
              placeholder="List any specific rules or requirements..."
              rows={2}
              style={{
                width: '100%',
                padding: '0.6rem',
                borderRadius: '8px',
                border: '1px solid rgba(147, 51, 234, 0.3)',
                background: 'rgba(30, 20, 50, 0.8)',
                color: '#fff',
                fontSize: '0.85rem',
                outline: 'none',
                resize: 'vertical',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading || !formData.title.trim()}
            style={{
              width: '100%',
              padding: '0.75rem',
              borderRadius: '8px',
              border: 'none',
              background: loading || !formData.title.trim() 
                ? 'rgba(147, 51, 234, 0.3)' 
                : 'linear-gradient(135deg, #a78bfa, #8b5cf6)',
              color: '#fff',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: loading || !formData.title.trim() ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Trophy size={18} />}
            {loading ? 'Posting...' : 'Post Contest'}
          </button>
        </form>
      </div>

      {/* Styles for gradient animation and scrollbar */}
      <style>{`
        @keyframes borderFlow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .post-contest-modal::-webkit-scrollbar {
          width: 5px;
        }
        .post-contest-modal::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.3);
          border-radius: 10px;
          margin: 12px 0;
        }
        .post-contest-modal::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #9333ea, #fbbf24);
          border-radius: 10px;
        }
      `}</style>
    </div>
  );
};

// ============================================
// LabelRadar Integration Tab Component
// ============================================
const LabelRadarIntegrationTab = () => {
  return (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      {/* Coming Soon Card */}
      <div style={{
        maxWidth: '500px',
        margin: '0 auto',
        background: 'rgba(30, 20, 50, 0.5)',
        border: '2px dashed rgba(147, 51, 234, 0.3)',
        borderRadius: '16px',
        padding: '3rem 2rem'
      }}>
        {/* Icon */}
        <div style={{
          width: '100px',
          height: '100px',
          borderRadius: '50%',
          background: 'rgba(147, 51, 234, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 1.5rem'
        }}>
          <Lock size={48} style={{ color: '#a78bfa', opacity: 0.5 }} />
        </div>

        {/* Title */}
        <h3 style={{ 
          color: '#a78bfa', 
          fontSize: '1.5rem', 
          fontWeight: '700',
          marginBottom: '1rem'
        }}>
          LabelRadar Integration
        </h3>

        {/* Description */}
        <p style={{ 
          color: 'rgba(255, 255, 255, 0.6)', 
          fontSize: '0.95rem',
          lineHeight: '1.6',
          marginBottom: '2rem'
        }}>
          We&apos;re working on getting API access from LabelRadar to bring you 
          real-time contest updates, direct demo submissions, and more advanced features.
        </p>

        {/* Coming Soon Badge */}
        <div style={{
          display: 'inline-block',
          padding: '0.5rem 1.5rem',
          borderRadius: '20px',
          background: 'rgba(147, 51, 234, 0.2)',
          border: '1px solid rgba(147, 51, 234, 0.4)',
          color: '#a78bfa',
          fontSize: '0.9rem',
          fontWeight: '600'
        }}>
          Coming Soon
        </div>
      </div>
    </div>
  );
};

// ============================================
// Main LabelConnectTab Component
// ============================================
const LabelConnectTab = () => {
  const [activeTab, setActiveTab] = useState('labelradar');

  const renderTabContent = () => {
    switch (activeTab) {
      case 'labelradar':
        return <RecordLabelsTab />;
      case 'local':
        return <LocalLabelsTab />;
      case 'private':
        return <PrivateLabelsTab />;
      case 'integration':
        return <LabelRadarIntegrationTab />;
      default:
        return <RecordLabelsTab />;
    }
  };

  return (
    <div style={{ minHeight: '100%' }}>
      {/* Sub-Tab Navigation */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        padding: '1rem',
        borderBottom: '1px solid rgba(251, 191, 36, 0.2)',
        flexWrap: 'wrap',
        background: 'rgba(30, 20, 50, 0.3)'
      }}>
        {SUB_TABS.map((tab) => {
          const IconComponent = tab.icon;
          const isActive = activeTab === tab.id;
          
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`label-connect-tab-${tab.id}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.6rem 1rem',
                borderRadius: '8px',
                border: isActive 
                  ? '2px solid rgba(251, 191, 36, 0.7)' 
                  : '1px solid rgba(251, 191, 36, 0.2)',
                background: isActive 
                  ? 'rgba(251, 191, 36, 0.15)' 
                  : 'rgba(255, 255, 255, 0.03)',
                color: isActive ? '#fbbf24' : 'rgba(255, 255, 255, 0.6)',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: isActive ? '600' : '400',
                transition: 'all 0.2s ease'
              }}
            >
              <IconComponent size={16} />
              <span>{tab.name}</span>
              {tab.id === 'labelradar' && (
                <span style={{ fontSize: '0.65rem', opacity: 0.6 }}>({tab.description})</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div style={{ minHeight: '400px' }}>
        {renderTabContent()}
      </div>
    </div>
  );
};

export default LabelConnectTab;
